import React, { useEffect, useState } from 'react'
import { Alert, Button, Paper, Snackbar, TextField, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import PageHeader from '../PageHeader'
import AdminSearch from './AdminSearch'
import PermissionPicker from './PermissionPicker'
import { buildPermissions, initialLevels } from './permissionsConfig'
import { groupCreationService } from '../../services/groupCreationService'
import ReactGA from 'react-ga4'

const GroupCreate = () => {
  const { groupId } = useParams()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [selectedAdmin, setSelectedAdmin] = useState(null)
  const [levels, setLevels] = useState(initialLevels)

  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' })

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window?.location?.pathname ?? '',
      title: 'Group Create Page'
    })
  }, [])

  const setLevel = (domainKey, level) => {
    setLevels(prev => ({ ...prev, [domainKey]: level }))
  }

  const isValid = name.trim() && joinCode.trim().length >= 5 && selectedAdmin

  const submit = async () => {
    if (!isValid) return
    setSubmitting(true)
    try {
      const res = await groupCreationService.createGroup(groupId, {
        name: name.trim(),
        joinCode: joinCode.trim(),
        adminUserId: selectedAdmin.id,
        permissions: buildPermissions(levels)
      })
      setToast({ open: true, severity: 'success', message: res.message || 'Group created' })
      setTimeout(() => navigate(`/dashboard/${groupId}/home`), 1200)
    } catch (err) {
      const message = err?.response?.data?.message || 'There was an error creating the group'
      setToast({ open: true, severity: 'error', message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader title='Create a Group' />
      <Paper className='p-6 max-w-2xl mx-auto' elevation={1}>
        <Typography variant='h6' className='mb-4'>Group details</Typography>

        <TextField
          variant='outlined'
          fullWidth
          label='Group Name'
          style={{ marginBottom: '20px' }}
          value={name}
          onChange={e => setName(e.target.value)}
        />

        <TextField
          variant='outlined'
          fullWidth
          label='Join Code'
          style={{ marginBottom: '20px' }}
          value={joinCode}
          error={!!joinCode && joinCode.trim().length < 5}
          helperText='Join code must be at least 5 characters.'
          onChange={e => setJoinCode(e.target.value)}
        />

        <Typography variant='h6' className='mt-4 mb-2'>Group admin</Typography>
        <AdminSearch
          groupId={groupId}
          selected={selectedAdmin}
          onSelect={setSelectedAdmin}
          onClear={() => setSelectedAdmin(null)}
        />

        <Typography variant='h6' className='mt-4 mb-2'>Admin permissions</Typography>
        <PermissionPicker levels={levels} onChange={setLevel} />

        <div className='flex justify-end gap-3 mt-6'>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant='contained' disabled={!isValid || submitting} onClick={submit}>
            {submitting ? 'Creating…' : 'Create Group'}
          </Button>
        </div>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default GroupCreate
